// Saved copies of the admin reads, for offline use (localStorage).
//
// One entry per read: key "djrAdminSaved:v1:<user id>:<endpoint>:<request>",
// value { savedAt, endpoint, data }. Every successful online fetch of a listed
// endpoint overwrites its entry; when a fetch fails offline the entry is
// served instead. Rules:
// - only the endpoints in SAVED_ENDPOINTS (admin reads), never auth calls;
// - only plain JSON (objects and arrays), never a file or a Blob;
// - never anything that carries an auth token (a top level token,
//   refreshToken or accessToken key), and the token itself is never stored:
//   it is only decoded in memory to find the signed-in user id;
// - scoped to the signed-in user id; no user, nothing saved or read;
// - capped: ENTRY_MAX_CHARS per entry, TOTAL_MAX_CHARS for all entries
//   (the oldest go first).

export const SAVED_PREFIX = "djrAdminSaved:v1:";
export const ENTRY_MAX_CHARS = 1500000;
export const TOTAL_MAX_CHARS = 3000000;

// Home (summary, events by year, rentals), Events list, the event page (event,
// documents, portal run sheet and requests, song requests), Requests inbox,
// Portal inbox. Not the portal and guest link status reads: they carry the
// client link tokens and keys.
export const SAVED_ENDPOINTS = new Set([
  "getAdminSummary",
  "getAllEvents",
  "getAdminRentals",
  "getEventById",
  "getEventAttachments",
  "getRunSheet",
  "listPortalRequests",
  "listSongRequestsAdmin",
  "getSongRequestsSummary",
  "getPortalInbox",
]);

const AUTH_KEYS = ["token", "refreshToken", "accessToken"];

const safeStorage = (storage) => {
  if (storage) return storage;
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

const decodePart = (part) => {
  const b64 = String(part ?? "").replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "===".slice((b64.length + 3) % 4);
  return typeof atob === "function" ? atob(padded) : "";
};

// The user id inside a JWT ({ user: { id } } from djr-be helpers/jwtTokens.js).
// Not verified: it only scopes the saved copies on this device.
export function userIdFromToken(token) {
  try {
    const parts = String(token ?? "").split(".");
    if (parts.length !== 3) return "";
    const payload = JSON.parse(decodePart(parts[1]));
    const id = payload?.user?.id ?? payload?.userId ?? payload?.id ?? payload?.sub ?? "";
    return typeof id === "string" || typeof id === "number" ? String(id) : "";
  } catch (e) {
    return "";
  }
}

export function currentUserId(storage) {
  const s = safeStorage(storage);
  try {
    return userIdFromToken(s?.getItem?.("token")) || userIdFromToken(s?.getItem?.("refreshToken"));
  } catch (e) {
    return "";
  }
}

// The request part of the key: url plus sorted params, method GET only.
export function requestKey(args) {
  if (typeof args === "string") return args;
  if (!args || typeof args !== "object") return "";
  const params = args?.params && typeof args.params === "object" ? args.params : {};
  const sorted = Object.keys(params)
    .sort()
    .filter((k) => params[k] !== undefined && params[k] !== null)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(String(params[k]))}`)
    .join("&");
  return `${String(args?.url ?? "")}${sorted ? `?${sorted}` : ""}`;
}

export const savedKey = (userId, endpoint, args) => `${SAVED_PREFIX}${userId}:${endpoint}:${requestKey(args)}`;

const isPlainJson = (data) => {
  if (data === null || typeof data !== "object") return false;
  if (Array.isArray(data)) return true;
  const proto = Object.getPrototypeOf(data);
  return proto === Object.prototype || proto === null;
};

export const carriesAuthToken = (data) =>
  Boolean(data) && typeof data === "object" && !Array.isArray(data) && AUTH_KEYS.some((k) => Object.prototype.hasOwnProperty.call(data, k));

export const canSave = (endpoint, data) => SAVED_ENDPOINTS.has(endpoint) && isPlainJson(data) && !carriesAuthToken(data);

function ourEntries(s) {
  const out = [];
  try {
    for (let i = 0; i < (s?.length ?? 0); i += 1) {
      const key = s.key(i);
      if (!key || !key.startsWith(SAVED_PREFIX)) continue;
      const raw = s.getItem(key) ?? "";
      let savedAt = 0;
      try {
        savedAt = Number(JSON.parse(raw)?.savedAt) || 0;
      } catch (e) {
        savedAt = 0;
      }
      out.push({ key, size: key.length + raw.length, savedAt });
    }
  } catch (e) {
    return out;
  }
  return out;
}

// Drop the oldest entries (never `keep`) until the total plus `extra` fits.
function makeRoom(s, keep, extra) {
  const entries = ourEntries(s).filter((e) => e.key !== keep).sort((a, b) => a.savedAt - b.savedAt);
  let total = entries.reduce((sum, e) => sum + e.size, 0) + extra;
  while (total > TOTAL_MAX_CHARS && entries.length > 0) {
    const old = entries.shift();
    try {
      s.removeItem(old.key);
    } catch (e) {
      // ignore
    }
    total -= old.size;
  }
}

// Save one read. Returns the savedAt time, or 0 when nothing was saved.
export function writeSaved({ storage, userId, endpoint, args, data, now = Date.now() } = {}) {
  const s = safeStorage(storage);
  if (!s || !userId || !SAVED_ENDPOINTS.has(endpoint)) return 0;
  const key = savedKey(userId, endpoint, args);
  if (!canSave(endpoint, data)) {
    // This answer may not be saved: the older copy goes too.
    try {
      s.removeItem(key);
    } catch (e) {
      // ignore
    }
    return 0;
  }
  let raw;
  try {
    raw = JSON.stringify({ savedAt: now, endpoint, data });
  } catch (e) {
    return 0;
  }
  if (!raw || key.length + raw.length > ENTRY_MAX_CHARS) {
    try {
      s.removeItem(key);
    } catch (e) {
      // ignore
    }
    return 0;
  }
  makeRoom(s, key, key.length + raw.length);
  try {
    s.setItem(key, raw);
    return now;
  } catch (e) {
    // Quota: drop the oldest half of ours and try once more.
    const entries = ourEntries(s).filter((x) => x.key !== key).sort((a, b) => a.savedAt - b.savedAt);
    entries.slice(0, Math.ceil(entries.length / 2)).forEach((x) => {
      try {
        s.removeItem(x.key);
      } catch (err) {
        // ignore
      }
    });
    try {
      s.setItem(key, raw);
      return now;
    } catch (err) {
      return 0;
    }
  }
}

// Read one saved copy: { data, savedAt } or null.
export function readSaved({ storage, userId, endpoint, args } = {}) {
  const s = safeStorage(storage);
  if (!s || !userId || !SAVED_ENDPOINTS.has(endpoint)) return null;
  try {
    const raw = s.getItem(savedKey(userId, endpoint, args));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt) || 0;
    if (!savedAt || !isPlainJson(parsed?.data)) return null;
    return { data: parsed.data, savedAt };
  } catch (e) {
    return null;
  }
}

// Remove every saved copy (all users), e.g. on sign out.
export function clearSaved(storage) {
  const s = safeStorage(storage);
  if (!s) return;
  ourEntries(s).forEach((e) => {
    try {
      s.removeItem(e.key);
    } catch (err) {
      // ignore
    }
  });
}
